import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  Check, AlertCircle, CreditCard, Calendar, X, ShieldAlert, Loader2,
} from 'lucide-react';

export default function SubscriptionManager() {
  const { agency, refreshAgency } = useAuth();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [error, setError] = useState('');

  const isActive = agency?.subscription_status === 'active';
  const isCanceled = agency?.subscription_status === 'canceled';
  const isPastDue = agency?.subscription_status === 'past_due';
  const isSuspended = agency?.subscription_status === 'suspended';
  const endsAt = agency?.subscription_ends_at;

  async function handleCancel() {
    setCanceling(true);
    setError('');

    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ agencyId: agency?.id }),
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to cancel');
      }

      await refreshAgency();
      setCancelOpen(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Subscription</h2>
        <p className="text-sm text-gray-500 mt-0.5">Manage your CareHire plan.</p>
      </div>

      {/* Current plan card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg font-bold text-gray-900">
                {agency?.billing_interval === 'yearly' ? '$480/year' : '$50/month'}
              </span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded ${
                isActive ? 'bg-green-100 text-green-700' : isPastDue ? 'bg-amber-100 text-amber-700' : isSuspended ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-600'
              }`}>
                {isActive ? 'Active' : isPastDue ? 'Past Due' : isSuspended ? 'Suspended' : 'Canceled'}
              </span>
            </div>
            <p className="text-sm text-gray-500">Full Access Plan — everything included</p>
          </div>
          <CreditCard className="w-8 h-8 text-gray-200" />
        </div>

        <div className="space-y-2 pt-4 border-t border-gray-100">
          {isActive && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Next billing date:</span>
              <span className="font-medium text-gray-700">
                {endsAt
                  ? new Date(endsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
                  : 'Monthly from signup'}
              </span>
            </div>
          )}
          {isCanceled && endsAt && (
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Access until:</span>
              <span className="font-medium text-gray-700">
                {new Date(endsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
          )}
          {isCanceled && (
            <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Your subscription is canceled. Your application link will stop accepting new
                submissions after the date above. Reactivate anytime to restore full access.
              </p>
            </div>
          )}
          {isPastDue && (
            <div className="flex items-start gap-2 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                Your last payment failed. Please email
                <a href="mailto:carehire@outlook.com" className="font-medium text-amber-800 underline">carehire@outlook.com</a> to restore
                full access. Your application link is still accepting submissions for now.
              </p>
            </div>
          )}
          {isSuspended && (
            <div className="flex items-start gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">
                Your subscription has been suspended due to repeated payment failures.
                Your application link is no longer accepting new submissions. Email
                <a href="mailto:carehire@outlook.com" className="font-medium text-red-700 underline">carehire@outlook.com</a> to restore access.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* What's included */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-5">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">What's Included</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            'Unlimited applications',
            'Custom screening form builder',
            'Full applicant dashboard',
            'CV upload & storage',
            'Email notifications',
            'Shareable link for any platform',
          ].map((f) => (
            <div key={f} className="flex items-center gap-2.5 text-sm text-gray-700">
              <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Cancel / Reactivate */}
      {isActive || isPastDue ? (
        <button
          onClick={() => setCancelOpen(true)}
          className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-md transition-colors"
        >
          <X className="w-4 h-4" />
          Cancel subscription
        </button>
      ) : (
        <div className="flex items-start gap-2.5 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <ShieldAlert className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">Want to reactivate?</p>
            <p className="text-xs text-blue-700">
              Email{' '}
              <a href="mailto:carehire@outlook.com" className="font-medium underline">carehire@outlook.com</a>{' '}
              to restore your subscription.
            </p>
          </div>
        </div>
      )}

      {/* Cancel confirmation modal */}
      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setCancelOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="w-11 h-11 bg-red-100 rounded-md flex items-center justify-center mb-4">
              <ShieldAlert className="w-6 h-6 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Cancel your subscription?</h3>
            <p className="text-sm text-gray-500 mb-1">
              Your application link <span className="font-mono text-gray-700">/apply/{agency?.slug}</span> will stop accepting new submissions at the end of your current billing period.
            </p>
            <p className="text-sm text-gray-500 mb-5">
              You'll still be able to sign in and view existing applicants, but no new applications will come through.
            </p>

            {error && (
              <div className="flex items-start gap-2.5 p-3 mb-4 rounded-md bg-red-50 border border-red-100">
                <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setCancelOpen(false)}
                className="flex-1 py-3 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-md transition-colors"
              >
                Keep my subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={canceling}
                className="flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60 rounded-md transition-colors"
              >
                {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                {canceling ? 'Canceling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
